import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  LinearProgress,
  Modal,
  ModalClose,
  Sheet,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import { useSnackbar } from 'notistack';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  getServiceTypes,
  createServiceType,
  updateServiceType,
} from '../../api';
import { CongregationServiceType } from '../../types/congregation';

export function AdminServiceTypes() {
  const { enqueueSnackbar } = useSnackbar();
  const [serviceTypes, setServiceTypes] = useState<CongregationServiceType[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingType, setEditingType] = useState<CongregationServiceType | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    loadServiceTypes();
  }, []);

  const loadServiceTypes = async () => {
    setLoading(true);
    try {
      const res = await getServiceTypes();
      setServiceTypes(res.serviceTypes || []);
    } catch (error) {
      enqueueSnackbar('Failed to load service types', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setEditingType(null);
  };

  const handleSaveType = async () => {
    if (!name.trim()) {
      enqueueSnackbar('Please enter a service type name', { variant: 'warning' });
      return;
    }

    try {
      if (editingType) {
        await updateServiceType(editingType.id, name, description);
        enqueueSnackbar('Service type updated successfully', { variant: 'success' });
      } else {
        await createServiceType(name, description);
        enqueueSnackbar('Service type created successfully', { variant: 'success' });
      }
      setShowDialog(false);
      resetForm();
      await loadServiceTypes();
    } catch (error) {
      enqueueSnackbar('Failed to save service type', { variant: 'error' });
    }
  };

  const handleEditType = (type: CongregationServiceType) => {
    setEditingType(type);
    setName(type.name);
    setDescription(type.description || '');
    setShowDialog(true);
  };

  return (
    <Stack spacing={3}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography level="h2">Service Types</Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Manage service type catalog
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="neutral"
            startDecorator={<RefreshIcon />}
            onClick={loadServiceTypes}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            variant="soft"
            color="primary"
            startDecorator={<AddIcon />}
            onClick={() => {
              resetForm();
              setShowDialog(true);
            }}
          >
            Add Service Type
          </Button>
        </Stack>
      </Stack>

      {/* Content */}
      {loading ? (
        <Box sx={{ p: 3 }}>
          <LinearProgress />
        </Box>
      ) : serviceTypes.length === 0 ? (
        <Card variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography level="body-lg" sx={{ color: 'text.secondary' }}>
            No service types found
          </Typography>
        </Card>
      ) : (
        <Sheet sx={{ overflow: 'auto', borderRadius: 'md' }}>
          <Table stickyHeader>
            <thead>
              <tr>
                <th style={{ width: '25%' }}>Name</th>
                <th style={{ width: '60%' }}>Description</th>
                <th style={{ width: '15%', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {serviceTypes.map((type) => (
                <tr key={type.id}>
                  <td>
                    <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                      {type.name}
                    </Typography>
                  </td>
                  <td>
                    <Typography
                      level="body-sm"
                      sx={{
                        maxWidth: '400px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {type.description || '-'}
                    </Typography>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => handleEditType(type)}
                      >
                        <EditIcon />
                      </Button>
                      <Button size="sm" variant="plain" color="danger" disabled>
                        <DeleteIcon />
                      </Button>
                    </Stack>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      )}

      {/* Create/Edit Dialog */}
      <Modal
        aria-labelledby="modal-title"
        open={showDialog}
        onClose={() => setShowDialog(false)}
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Card
          variant="outlined"
          sx={{
            maxWidth: 500,
            borderRadius: 'md',
            p: 3,
            boxShadow: 'lg',
          }}
        >
          <ModalClose variant="plain" sx={{ m: 1 }} />
          <Typography
            level="h4"
            sx={{
              mb: 2,
              textAlign: 'left',
            }}
          >
            {editingType ? 'Edit Service Type' : 'Add Service Type'}
          </Typography>
          <Stack spacing={2}>
            <FormControl>
              <FormLabel>Name *</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Service type name (e.g., Cooking, Cleaning, Teaching)"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this service type"
                minRows={3}
              />
            </FormControl>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="neutral"
                onClick={() => setShowDialog(false)}
              >
                Cancel
              </Button>
              <Button variant="soft" color="primary" onClick={handleSaveType}>
                {editingType ? 'Update' : 'Create'}
              </Button>
            </Stack>
          </Stack>
        </Card>
      </Modal>
    </Stack>
  );
}
